<script setup>
import { ref } from 'vue'
import { useToast } from 'vue-toastification'

const props = defineProps({
  dashboard: {
    type: Object,
    required: true
  },
  onDelete: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()

const deleting = ref(false)

async function doDelete() {
  if (deleting.value) return
  deleting.value = true
  try {
    await props.onDelete([])
    emit('modal-close')
  } catch (e) {
    toast.error(e.message || 'Failed to delete dashboard')
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <div class="delete-modal">
    <h3 class="modal-title">Delete Dashboard</h3>
    <p class="modal-description">
      Are you sure you want to delete <strong>{{ dashboard.name }}</strong>? This cannot be undone.
    </p>

    <div class="modal-actions">
      <button
        class="btn-delete"
        :disabled="deleting"
        @click="doDelete"
      >
        <font-awesome-icon v-if="deleting" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'trash']" />
        {{ deleting ? 'Deleting...' : 'Delete' }}
      </button>
      <button class="btn-close-modal" @click="emit('modal-close')">
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.delete-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0 0 1rem 0;
}

.modal-description strong {
  color: var(--text-primary);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn-delete {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: var(--radius-sm);
  color: var(--color-error);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-delete:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-close-modal {
  padding: 0.5rem 1rem;
  background: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
}
</style>
